import React from 'react'
import classNames from 'classnames'
import { useThemeContext } from '@/contexts/ThemeContext'

export interface ProgressBarProps extends React.ComponentPropsWithRef<'div'> {
  /**
   * Whether or not to collapse the progress bar
   *
   * @optional
   * @type boolean
   */
  active?: boolean
  /**
   * Classes to apply to the indicator bar element
   *
   * @optional
   * @type string
   */
  barClassName?: string
  /**
   * The max value of the progress bar
   *
   * @optional
   * @type number
   * @default 100
   */
  max?: number
  /**
   * The min value of the progress bar
   *
   * @optional
   * @type number
   * @default 0
   */
  min?: number
  /**
   * The current value of the progress bar
   *
   * @type number
   */
  value: number
}

/**
 * A component that displays a progress bar with customizable values and styles
 * @param {ProgressBarProps} props - The props for the component
 */
export const ProgressBar = (props: ProgressBarProps) => {
  const { barClassName, className, active = true, value, max = 100, min = 0, ...rest } = props

  const { isDarkMode } = useThemeContext()

  const clampedValue = Math.min(max, Math.max(min, value))
  const clampedProgress = max > min ? ((clampedValue - min) / (max - min)) * 100 : 0

  const containerClassName = classNames(
    'h-3 overflow-hidden transition-width ease-in-out duration-1000',
    isDarkMode ? 'bg-white/30' : 'bg-black/20',
    active ? 'w-full rounded-xl' : 'w-3 rounded-full',
    className,
  )

  const progressBarClassName = classNames(
    'rounded-xl h-full transition-all ease-linear duration-100',
    isDarkMode ? 'bg-white' : 'bg-black',
    !active && 'hidden',
    barClassName,
  )

  return (
    <div
      className={containerClassName}
      role="progressbar"
      aria-valuemin={min}
      aria-valuemax={max}
      aria-valuenow={clampedValue}
      {...rest}
    >
      <div className={progressBarClassName} style={{ width: `${clampedProgress}%` }}></div>
    </div>
  )
}
